/**
 * Documentation Page
 * Renders markdown documentation with navigation
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './Documentation.css';

// Import markdown content as raw strings
import quickStartMd from '../../docs/guides/QUICK_START.md?raw';
import userGuideMd from '../../docs/guides/USER_GUIDE.md?raw';
import adminGuideMd from '../../docs/guides/ADMIN_GUIDE.md?raw';

const docs = {
  'quick-start': {
    title: 'Quick Start Guide',
    content: quickStartMd,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
  'user-guide': {
    title: 'User Guide',
    content: userGuideMd,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  'admin-guide': {
    title: 'Admin Guide',
    content: adminGuideMd,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
};

// Custom image component to handle relative paths
const ImageComponent = ({ src, alt }) => {
  // Convert relative markdown paths to actual image imports
  if (src && src.startsWith('../screenshots/')) {
    const filename = src.replace('../screenshots/', '');
    return (
      <img
        src={`/docs/screenshots/${filename}`}
        alt={alt}
        className="doc-image"
        loading="lazy"
      />
    );
  }
  return <img src={src} alt={alt} className="doc-image" loading="lazy" />;
};

const Documentation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentDoc = searchParams.get('doc') || 'quick-start';
  const [activeSection, setActiveSection] = useState('');

  const doc = docs[currentDoc];

  // Extract headings for table of contents
  const extractHeadings = (markdown) => {
    const headingRegex = /^(#{1,3})\s+(.+)$/gm;
    const headings = [];
    let match;
    while ((match = headingRegex.exec(markdown)) !== null) {
      const level = match[1].length;
      const text = match[2];
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      headings.push({ level, text, id });
    }
    return headings;
  };

  const headings = doc ? extractHeadings(doc.content) : [];

  // Handle scroll spy for active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [currentDoc, headings]);

  // Custom heading renderer to add IDs
  const HeadingComponent = ({ level, children }) => {
    const text = children?.toString() || '';
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const Tag = `h${level}`;
    return <Tag id={id}>{children}</Tag>;
  };

  if (!doc) {
    return (
      <div className="documentation">
        <div className="doc-not-found">
          <h1>Documentation Not Found</h1>
          <p>The requested documentation page does not exist.</p>
          <Link to="/dashboard/docs?doc=quick-start" className="btn btn-primary">
            Go to Quick Start
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="documentation">
      {/* Sidebar */}
      <aside className="doc-sidebar">
        <div className="doc-sidebar-header">
          <h2>Documentation</h2>
        </div>

        <nav className="doc-nav">
          <div className="doc-nav-section">
            <h3>Guides</h3>
            {Object.entries(docs).map(([key, value]) => (
              <button
                key={key}
                className={`doc-nav-item ${currentDoc === key ? 'active' : ''}`}
                onClick={() => setSearchParams({ doc: key })}
              >
                {value.icon}
                <span>{value.title}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* Table of Contents */}
        {headings.length > 0 && (
          <div className="doc-toc">
            <h3>On this page</h3>
            <ul>
              {headings.filter(h => h.level <= 2).map(({ text, id, level }) => (
                <li key={id} className={`toc-level-${level} ${activeSection === id ? 'active' : ''}`}>
                  <a href={`#${id}`}>{text}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="doc-content">
        <div className="doc-header">
          <div className="doc-breadcrumb">
            <Link to="/dashboard">Dashboard</Link>
            <span>/</span>
            <span>Documentation</span>
            <span>/</span>
            <span>{doc.title}</span>
          </div>
        </div>

        <article className="doc-article">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              img: ImageComponent,
              h1: (props) => <HeadingComponent level={1} {...props} />,
              h2: (props) => <HeadingComponent level={2} {...props} />,
              h3: (props) => <HeadingComponent level={3} {...props} />,
              a: ({ href, children }) => {
                // Handle internal doc links
                if (href && href.startsWith('./')) {
                  const docKey = href.replace('./', '').replace('.md', '').toLowerCase().replace(/_/g, '-');
                  return (
                    <Link to={`/dashboard/docs?doc=${docKey}`} className="doc-link">
                      {children}
                    </Link>
                  );
                }
                // External links
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                );
              },
            }}
          >
            {doc.content}
          </ReactMarkdown>
        </article>

        {/* Navigation Footer */}
        <div className="doc-footer">
          {Object.keys(docs).map((key, index, arr) => {
            if (key === currentDoc) {
              return (
                <div key={key} className="doc-footer-nav">
                  {index > 0 && (
                    <button
                      className="doc-footer-btn prev"
                      onClick={() => setSearchParams({ doc: arr[index - 1] })}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      <div>
                        <span>Previous</span>
                        <strong>{docs[arr[index - 1]].title}</strong>
                      </div>
                    </button>
                  )}
                  {index < arr.length - 1 && (
                    <button
                      className="doc-footer-btn next"
                      onClick={() => setSearchParams({ doc: arr[index + 1] })}
                    >
                      <div>
                        <span>Next</span>
                        <strong>{docs[arr[index + 1]].title}</strong>
                      </div>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>
      </main>
    </div>
  );
};

export default Documentation;
